import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { PWA_PROTOCOL_SCHEME } from "./config";
import { goToPendingOrFallback } from "./continueAfterAuth";

const ALLOWED_COMMANDS = new Set(["open", "continue"]);
const ALLOWED_PLATFORMS = new Set(["psl", "biz"]);

/**
 * Parse untrusted ?url= from protocol_handlers into a safe intent.
 * Never redirect to the decoded string itself.
 *
 * ACCEPT: web+pofsis://open|continue/{psl|biz}
 * REJECT: anything else (other schemes, commands, platforms, nested paths)
 */
export const parsePofsisProtocolIntent = (raw) => {
	if (!raw || typeof raw !== "string") return null;

	let decoded;
	try {
		decoded = decodeURIComponent(raw.trim());
	} catch {
		return null;
	}

	let parsed;
	try {
		parsed = new URL(decoded);
	} catch {
		return null;
	}

	if (parsed.protocol !== `${PWA_PROTOCOL_SCHEME}:`) return null;
	if (parsed.username || parsed.password) return null;

	const command = (parsed.hostname || "").toLowerCase();
	const pathParts = (parsed.pathname || "/")
		.split("/")
		.filter(Boolean)
		.map((p) => p.toLowerCase());

	if (pathParts.length !== 1) return null;

	const platform = pathParts[0];
	if (!ALLOWED_COMMANDS.has(command)) return null;
	if (!ALLOWED_PLATFORMS.has(platform)) return null;

	return { command, platform };
};

/**
 * Manifest protocol_handlers receiver: /pwa/launch?url=%s
 * Maps allowlisted web+pofsis intents to trusted in-app actions only.
 */
export const ProtocolLaunchPage = () => {
	const [message, setMessage] = useState("Opening…");

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const raw = params.get("url");
		const intent = parsePofsisProtocolIntent(raw);

		if (!intent) {
			if (process.env.NODE_ENV !== "production") {
				console.warn("[PWA] Rejected protocol launch url:", raw);
			}
			setMessage("Invalid launch link. Redirecting…");
			window.location.replace("/");
			return;
		}

		const { command, platform } = intent;

		if (command === "continue") {
			setMessage("Continuing…");
			goToPendingOrFallback(platform);
			return;
		}

		// open → in-app platform route (trusted path only)
		setMessage("Opening…");
		window.location.replace(`/${platform}`);
	}, []);

	return (
		<Box
			minHeight="100vh"
			display="flex"
			alignItems="center"
			justifyContent="center"
			px={2}
			sx={{
				background:
					"linear-gradient(135deg, #d8f3e8 0%, #f7fbf9 42%, #ffffff 100%)",
			}}
		>
			<Typography sx={{ color: "#64748b", fontSize: 15 }}>{message}</Typography>
		</Box>
	);
};

export default ProtocolLaunchPage;
